<template>
  <v-container
    class="fill-height"
    fluid
  >
    <v-row
      align="center"
      justify="center"
    >
      <v-col
        cols="12"
        class="centerPlace"
      >
        <v-card elevation="12" class="rounded-xl auth-glass-card">
          <v-toolbar
            color="transparent"
            flat
            height="64"
            class="px-2"
          >
            <v-toolbar-title class="font-weight-bold">
              <div class="d-flex align-center">
                <v-img
                  :src="logo"
                  width="40"
                  height="40"
                  max-width="40"
                  class="mr-2 flex-grow-0 flex-shrink-0"
                />
                <span>Créer un compte</span>
              </div>
            </v-toolbar-title>
          </v-toolbar>
          <v-card-text v-if="!successMessage">
            <template v-if="!gInfoOK">
              <v-row class="mt-1">
                <v-col cols="12" align-self="center" class="text-center">
                  <v-btn
                    color="white"
                    elevation="1"
                    block
                    @click="signupWithGoogle"
                  >
                    <v-icon class="mr-2" :color="'#'+changingColor">
                      mdi-google
                    </v-icon>
                    Inscription avec Google
                  </v-btn>
                </v-col>
              </v-row>
              <v-row align="center" class="my-2">
                <v-col>
                  <v-divider />
                </v-col>
                <v-col cols="auto" class="pa-0 text-center text-caption text-medium-emphasis">
                  <span class="mx-2 font-weight-medium">OU</span>
                </v-col>
                <v-col>
                  <v-divider />
                </v-col>
              </v-row>
              <v-form ref="form">
                <div class="mb-1 px-1">
                  <label for="name" class="text-body-2 font-weight-medium">Nom</label>
                </div>
                <v-text-field
                  id="name"
                  v-model="username"
                  placeholder="Votre nom"
                  prepend-inner-icon="mdi-account"
                  type="text"
                  autocomplete="off"
                  data-lpignore="true"
                  class="mb-3"
                  bg-color="background"
                  :rules="[rules.required]"
                />

                <div class="mb-1 px-1">
                  <label for="signup-email" class="text-body-2 font-weight-medium">Email</label>
                </div>
                <v-text-field
                  id="signup-email"
                  v-model="email"
                  name="email"
                  placeholder="exemple@email.com"
                  prepend-inner-icon="mdi-at"
                  type="text"
                  autocomplete="username"
                  class="mb-3"
                  bg-color="background"
                  :rules="[rules.required]"
                />

                <div class="mb-1 px-1">
                  <label for="signup-password" class="text-body-2 font-weight-medium">Mot de passe</label>
                </div>
                <v-text-field
                  id="signup-password"
                  v-model="password"
                  name="password"
                  placeholder="Minimum 8 caractères"
                  prepend-inner-icon="mdi-lock"
                  :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  autocomplete="new-password"
                  class="mb-3"
                  bg-color="background"
                  :rules="[rules.required, rules.min]"
                  @click:append-inner="showPassword = !showPassword"
                />
              </v-form>
            </template>
            <template v-else>
              <v-row justify="center" class="my-2">
                <v-col cols="auto" class="text-center">
                  <v-icon size="x-large" color="success" class="mr-1">
                    mdi-account-check-outline
                  </v-icon>
                  <span class="font-weight-medium">Informations Google validées</span>
                </v-col>
              </v-row>
            </template>
            <v-divider class="my-4" />
            <v-form ref="communityForm">
              <div class="text-center text-subtitle-1 font-weight-bold mb-2">
                Communauté
                <VDropdown
                  :triggers="['hover']"
                  class="inlineTooltip ml-1"
                >
                  <v-icon
                    size="16"
                    color="grey-lighten-1"
                  >
                    mdi-help-circle-outline
                  </v-icon>
                  <template #popper>
                    <p class="text-caption communityDesc mb-0">
                      Vous devez obligatoirement <b>rejoindre</b> ou <b>créer</b> une communauté pour partager un même menu.
                      Pour en rejoindre une, demandez son code à l'un de ses membres.
                    </p>
                  </template>
                </VDropdown>
              </div>
              <v-tabs
                v-model="communityTab"
                fixed-tabs
                class="mb-3"
              >
                <v-tab>
                  Rejoindre
                </v-tab>
                <v-tab>
                  Créer
                </v-tab>
              </v-tabs>
              <v-window
                v-model="communityTab"
              >
                <v-window-item>
                  <v-text-field
                    id="communityCode"
                    v-model="communityCode"
                    label="Code de communauté"
                    name="communityCode"
                    prepend-inner-icon="mdi-account-group"
                    type="text"
                    bg-color="background"
                  />
                </v-window-item>
                <v-window-item>
                  <v-text-field
                    id="communityName"
                    v-model="communityName"
                    label="Nom de la communauté"
                    name="communityName"
                    prepend-inner-icon="mdi-account-multiple-plus"
                    type="text"
                    bg-color="background"
                  />
                </v-window-item>
              </v-window>
              <vue-recaptcha
                ref="recaptcha"
                size="invisible"
                sitekey="6Lc9qsoUAAAAAEKXMmhEh4CIGnNO1xmQZ0O6bd4w"
                @verify="onCaptchaVerified"
                @expired="onCaptchaExpired"
              />
            </v-form>

            <v-row dense class="mt-4 text-center">
              <v-col cols="12">
                <v-btn
                  color="primary"
                  size="large"
                  block
                  :loading="submitting"
                  @click="signup"
                >
                  Créer mon compte
                </v-btn>
              </v-col>
            </v-row>

            <v-divider class="my-5" />

            <v-row class="text-center">
              <v-col cols="12" class="text-body-2 text-medium-emphasis">
                Déjà un compte ?
                <router-link
                  to="/login"
                  class="text-primary font-weight-bold text-decoration-none ml-1 link-hover"
                >
                  Se connecter
                </router-link>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-text v-if="successMessage" class="text-center py-6">
            <v-icon size="64" color="success" class="mb-4">
              mdi-email-check-outline
            </v-icon>
            <h3 class="text-h6 font-weight-bold mb-2">
              Presque terminé !
            </h3>
            <p class="text-body-2 text-medium-emphasis mb-6">
              Vous allez recevoir un email de confirmation pour finaliser la création de votre compte.
            </p>
            <v-row dense class="text-center">
              <v-col cols="12">
                <v-btn
                  color="primary"
                  size="large"
                  block
                  to="/login"
                >
                  Se connecter
                </v-btn>
              </v-col>
              <v-col cols="12" class="mt-2">
                <v-btn
                  variant="text"
                  color="primary"
                  size="small"
                  @click="resend"
                >
                  Renvoyer l'email
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { useRepo } from 'pinia-orm'
import { UserRepo } from '@/models/User'
import { VueRecaptcha } from 'vue-recaptcha'
import { useToast } from 'vue-toastification'
import logoImg from '@/assets/logo.png'

export default {
  name: 'SignUpView',
  components: {
    VueRecaptcha
  },
  beforeRouteEnter: function(to, from, next) {
    document.body.classList.add('login')
    next()
  },
  beforeRouteLeave: function(to, from, next) {
    document.body.classList.remove('login')
    next()
  },
  data: () => ({
    logo: logoImg,
    username: '',
    email: '',
    password: '',
    communityTab: null,
    communityCode: '',
    communityName: '',
    gAuthCode: '',
    gInfoOK: false,

    showPassword: false,
    rules: {
      required: v => !!v || 'Obligatoire',
      min: v => v.length >= 8 || 'Min 8 charactères'
    },
    submitting : false,
    successMessage: false,

    changingColor: '',
    changingColorInterval: null
  }),
  computed: {
    currentBreakpoint () {
      const bps = ['xs', 'sm', 'md', 'lg', 'xl']
      const bp = bps.filter(b => this.$vuetify.display[b])
      return bp.length ? bp[0] : '';
    }
  },
  mounted() {
    // Blue, red, yellow, green
    const gColor = ['4285F4', 'DB4437', 'F4B400', '0F9D58']
    this.changingColorInterval = setInterval(() => {
      const prevColorIndex = gColor.indexOf(this.changingColor)
      this.changingColor = !this.changingColor ? gColor[0] : gColor[(prevColorIndex+1) % 4]
    }, 2000);
  },
  beforeUnmount() {
    clearInterval(this.changingColorInterval)
  },
  methods: {
    isValidForm () {
      return (this.gAuthCode || this.$refs.form.validate()) &&
             ((this.communityTab === 0 && this.communityCode) ||
              (this.communityTab === 1 && this.communityName))
    },
    signup: async function () {
      if(!this.isValidForm()) {
        const toast = useToast()
        toast("Le formulaire n'est pas correctement rempli.", {
          type: 'error'
        })
        return;
      }

      this.submitting = true
      this.$refs.recaptcha.execute()
    },
    onCaptchaVerified: async function(token) {
      this.$refs.recaptcha.reset()

      try {
        await useRepo(UserRepo).$create({
          name: this.username,
          email: this.email,
          password: this.password,
          communityCode: this.communityTab === 0 ? this.communityCode : '',
          communityName: this.communityTab === 1 ? this.communityName : '',
          recaptcha: token,
          gAuthCode: this.gAuthCode
        })
        this.successMessage = true
      }
      catch(e) {
        const toast = useToast()
        toast('Nous n\'avons pas pu créer cet utilisateur...', {
          type: 'error'
        })
      }
      this.submitting = false
    },
    onCaptchaExpired() {
      this.$refs.recaptcha.reset()
    },
    resend () {
      if(this.email) {
        useRepo(UserRepo).$validate(this.email)
      }
    },
    async signupWithGoogle () {
      try {
        this.gAuthCode = await this.$gAuth.getAuthCode()
        this.gInfoOK = true
      } catch (error) {
        debugger
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.centerPlace {
  max-width: 480px;
}

.communityDesc {
  max-width: 300px;
  padding: 5px;
}

.inlineTooltip {
  display: inline-block;
}

.link-hover {
  transition: opacity 0.2s ease;
  &:hover {
    text-decoration: underline !important;
    opacity: 0.85;
  }
}
</style>